import type { CountryCode } from "libphonenumber-js";

import { removeMdnFormatting } from "@expert/common-ui";
import { useCallback, useEffect, useMemo } from "react";

import { useSessionActiveCallerId } from "@/sdk";
import { formatPhoneNumber, useAsyncLoading, validatePhoneNumber } from "@/shared-utils";

import type { CallbackSurface } from "../../types";

import { useCallbacksFormStore } from "../../callbacksForm.store";
import { useScheduleCallback } from "../../useScheduleCallback";

interface UseScheduleCallbackFormOptions {
    callbackSurface: CallbackSurface;
    afterSubmitClickDefault?: () => void;
    defaultCountry?: CountryCode;
    /** Runs after the form store resets, before the caller id is applied. */
    applyDefaults: () => void;
    /** Fixed reason category. Recorded on `CallbackScheduled` when scheduling succeeds. */
    callbackReason?: string;
}

/** Phone number, validation, and submit behavior shared by the callback forms. */
export function useScheduleCallbackForm({
    callbackSurface,
    afterSubmitClickDefault,
    defaultCountry,
    applyDefaults,
    callbackReason,
}: UseScheduleCallbackFormOptions) {
    const selectedCallbackMdn = useCallbacksFormStore((state) => state.selectedCallbackMdn);
    const callbackScheduled = useCallbacksFormStore((state) => state.callbackScheduled);
    const selectedCallbackOption = useCallbacksFormStore((state) => state.selectedCallbackOption);
    const { setSelectedCallbackMdn } = useCallbacksFormStore.getState();

    const { scheduleCallback } = useScheduleCallback();
    const activeSessionMdn = useSessionActiveCallerId();

    const defaultMdn = useMemo(
        () =>
            callbackScheduled && selectedCallbackMdn
                ? formatPhoneNumber(selectedCallbackMdn, defaultCountry, true).number || activeSessionMdn
                : activeSessionMdn,
        // oxlint-disable-next-line exhaustive-deps
        [activeSessionMdn],
    ); //? calculates once only when panel is mounted or activeSessionMdn changes

    useEffect(() => {
        if (!callbackScheduled) {
            useCallbacksFormStore.getState().resetState();
            applyDefaults();
            if (activeSessionMdn) setSelectedCallbackMdn(activeSessionMdn);
        } else if ((!selectedCallbackMdn || !validatePhoneNumber(selectedCallbackMdn).valid) && activeSessionMdn) {
            setSelectedCallbackMdn(activeSessionMdn);
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [activeSessionMdn]); //? runs only when panel is mounted or activeSessionMdn changes

    const [isSubmitting, onSubmit] = useAsyncLoading(async () => {
        await (callbackReason ? scheduleCallback(callbackSurface, callbackReason) : scheduleCallback(callbackSurface));
        if (afterSubmitClickDefault) afterSubmitClickDefault();
    });

    const isCallbackMdnValid = selectedCallbackMdn
        ? validatePhoneNumber(selectedCallbackMdn, defaultCountry).valid
        : false;

    const onMdnInput = useCallback(
        (event: React.InputEvent<HTMLInputElement>) => {
            setSelectedCallbackMdn(removeMdnFormatting(event.currentTarget.value));
        },
        [setSelectedCallbackMdn],
    );

    return {
        selectedCallbackMdn,
        selectedCallbackOption,
        defaultMdn,
        isSubmitting,
        isCallbackMdnValid,
        onSubmit,
        onMdnInput,
    };
}
